import React, { useState, useEffect } from "react";
import CustomDataGrid from "../components/CustomDataGrid";
import api from "./PlanMock";
import {
  generateGridRows,
  generateGridColumns,
  usageMetricGridOptionalColumns,
  usageMetricsRowMapping,
  usageMetricGridColumns,
  generateFormData,
  usageMetricMapping,
} from "../utils/PageUtils";
import EntityDetails from "../components/EntityDetails";
import QuotaBarChart from "../components/Barchart";
import { Alert, Tab, Tabs } from "react-bootstrap";
import "../styles/UsageTracker.css";
import PageHelmet from "../components/PageHelmet";
import { FiFileText, FiBarChart2 } from "react-icons/fi"; // icons for tabs
import { MdArrowBackIos } from "react-icons/md";

const UsageTracker = ({userRole, bearerToken}) => {
  const [usageMetric, setUsageMetric] = useState([]);
  const [usageMetricError, setUsageMetricError] = useState(null);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [selectedUsageMetric, setSelectedUsageMetric] = useState(null);
  const [quotaData, setQuotaData] = useState([]);
  const [activeTab, setActiveTab] = useState("details"); // Default tab

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    if (selectedUsageMetric && isMobile) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [selectedUsageMetric, isMobile]);

  useEffect(() => {
    const fetchQuotaUsages = async () => {
      try {
        const response = await api.get("/usage-metrics");
        if (response.status === 200 && response.data.length > 0) {
          setUsageMetric(response.data);
          // Don't auto-select on mobile; let user click a row
          if (!isMobile) {
            setSelectedUsageMetric(response.data[0]); // Select first row by default on desktop
            setSelectedRowId(1);
          }
        } else {
          setUsageMetricError("Failed to fetch Usage Metrics");
        }
      } catch (error) {
        setUsageMetricError("Failed to fetch usage metrics");
      }
    };
    fetchQuotaUsages();
  }, [isMobile]);

  useEffect(() => {
    const selected = selectedUsageMetric || usageMetric[0]; // fallback to first row
    if (selected) {
      const data = Object.keys(selected.quota || {}).map((key) => {
        const used = Number(selected.used_quota?.[key] || 0);
        const total = Number(selected.quota?.[key] || 0);
        const unused = total - used;
        return {
          name: key.charAt(0).toUpperCase() + key.slice(1),
          used,
          unused: unused < 0 ? 0 : unused,
        };
      });
      setQuotaData(data);
    } else {
      setQuotaData([]);
    }
  }, [usageMetric, selectedUsageMetric]);

  const handleRowClick = (e) => {
    const rowId = e?.row?.id ?? e?.id;
    if (selectedRowId === rowId) {
      setSelectedRowId(null);
      setSelectedUsageMetric(null);
    } else {
      const selected = usageMetric[e.id - 1];
      setSelectedUsageMetric(selected);
      setSelectedRowId(rowId);
    }
    console.log("Selected Row ID:", rowId);
    setActiveTab("details"); // Reset to "Details" tab on new selection
  };

  const handleCloseDetails = () => {
    setSelectedUsageMetric(null);
    setSelectedRowId(null);
  };

  // Inline helper for Tabs
  function UsageDetailsTabs({ activeTab, setActiveTab, details, data }) {
    return (
      <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="usage-tabs mb-4">
        <Tab
          eventKey="details"
          title={
            <span className="tab-title">
              <FiFileText style={{ marginRight: 6 }} />
              Details
            </span>
          }
        >
          <div className="usage-metric-details">
            <EntityDetails inputs={details} width={"140px"} />
          </div>
        </Tab>

        <Tab
          eventKey="quota"
          title={
            <span className="tab-title">
              <FiBarChart2 style={{ marginRight: 6 }} />
              Quota Chart
            </span>
          }
        >
          <div className="usage-tracker-chart">
            <QuotaBarChart data={data} />
          </div>
        </Tab>
      </Tabs>
    );
  }

  const usageMetricForGrid = usageMetric.map((p) => ({
    ...p,
    quota:
      typeof p.quota === "object" && p.quota !== null
        ? Object.entries(p.quota)
            .map(([k, v]) => `${k}: ${v}`)
            .join("\n")
        : p.quota,
    used_quota:
      typeof p.used_quota === "object" && p.used_quota !== null
        ? Object.entries(p.used_quota)
            .map(([k, v]) => `${k}: ${v}`)
            .join("\n")
        : p.used_quota,
  }));

  const usageMetricRows = generateGridRows(
    usageMetricForGrid,
    usageMetricsRowMapping
  );
  const usageMetricColumns = generateGridColumns(
    usageMetricGridColumns,
    "usage_metric_id",
    undefined,
    usageMetricGridOptionalColumns
  );

  const usageMetricFormData = selectedUsageMetric
    ? generateFormData(selectedUsageMetric, usageMetricMapping)
    : [];

 if (userRole !== "customer") {
        return (
        <div className="user-error">
            <h1>404 - Page Not Found</h1>
            <p>The page you're looking for doesn't exist.</p>
        </div>
        );
    }
  return (
    <div className="usage-tracker-page">
      <PageHelmet
        title="Usage Tracker"
        description="View Quota Usages"
        url={`${process.env.REACT_APP_COGNITO_REDIRECT_URL}/usage_tracker`}
      />

      {usageMetricError && <Alert variant="danger">{usageMetricError}</Alert>}

      <div
        className={`usage-tracker-container ${
          selectedUsageMetric ? "with-selection" : "no-selection"
        }`}
      >
        {!usageMetricError && (
          <div className="usage-metric-grid">
            <h2 className="usage-tracker-heading">Usage Tracker</h2>
            <CustomDataGrid
              rows={usageMetricRows}
              columns={usageMetricColumns}
              totalRows={usageMetric.length}
              onRowClick={handleRowClick}
              selectedRowId={selectedRowId}
                selectionModel={selectedRowId ? [selectedRowId] : []}  // <- ensures highlight

              getRowHeight={() => "auto"}
            />
          </div>
        )}

        {selectedUsageMetric && (
          <div className={`usage-metric-details-wrapper ${isMobile ? 'mobile-open' : ''}`}>
            <div className="usage-metric-details-header">
              <div className="mobile-detail-nav">
                <button className="back-btn" onClick={handleCloseDetails} aria-label="Back">
                  <MdArrowBackIos size={18} />
                </button>
                <div className="mobile-activity-title">{selectedUsageMetric?.usage_metric_id || selectedUsageMetric?.customer_id || 'Details'}</div>
              </div>
            </div>

            <div className="usage-metric-details-body">
              <UsageDetailsTabs activeTab={activeTab} setActiveTab={setActiveTab} details={usageMetricFormData} data={quotaData} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UsageTracker;
